/**
 * Silhouette generator library — the reusable core that turns a `shape` name +
 * a seed into a creature/structure silhouette (normalized geometry). The
 * `leviathans` kind consumes it; future kinds/biomes reuse it by adding a
 * generator, not new rendering code.
 *
 * Pattern: a self-registering generator map (like EFFECT_REGISTRY). Adding a
 * shape = add a file + one entry in the map. `generateSilhouette` dispatches.
 *
 * Render-agnostic: returns geometry (points), not Pixi objects. The consumer
 * draws + bakes. Deterministic: same seed ⇒ identical geometry.
 *
 * @see ./whaleSkeleton.ts — v1 generator.
 * @see docs/design/systems/silhouette-generator-library.md — blueprint.
 */
import { whaleSkeleton } from './whaleSkeleton';
import { serpentSkeleton } from './serpentSkeleton';
import { mantaSkeleton } from './mantaSkeleton';
import type { Point } from './types';

export type SilhouetteShape = 'whaleSkeleton' | 'serpentSkeleton' | 'mantaSkeleton';

export interface SilhouetteParams {
  shape: SilhouetteShape;
  seed: number;
  /** Target bounding width in px (height derived by the generator's aspect). */
  width: number;
  /** 0.5..1.5 — rib count / curve fidelity multiplier (default 1). */
  detail?: number;
}

export interface SilhouetteGeometry {
  /** Closed body hull (for fill + rim-stroke), in target px coordinates. */
  outline: Point[];
  /** Sub-parts (ribs, spine, flukes) as stroke polylines, in target px. */
  parts: Array<{ points: Point[] }>;
  /** Bounding box in target px. */
  bbox: { w: number; h: number };
}

/** The registry: shape name → generator function. Self-registering pattern. */
const GENERATORS: Record<SilhouetteShape, (seed: number, detail: number) => {
  outline: Point[];
  parts: Array<{ points: Point[] }>;
  bbox: { w: number; h: number };
}> = {
  whaleSkeleton,
  serpentSkeleton,
  mantaSkeleton,
};

/** All registered shape names (conformance-testable). */
export const SILHOUETTE_SHAPES: SilhouetteShape[] = Object.keys(GENERATORS) as SilhouetteShape[];

/**
 * Generate a silhouette at the target pixel width. The generator produces
 * normalized 0..1 geometry; this scales it to `width` px.
 */
export function generateSilhouette(p: SilhouetteParams): SilhouetteGeometry {
  const gen = GENERATORS[p.shape];
  if (!gen) throw new Error(`Unknown silhouette shape: ${p.shape}`);
  const detail = p.detail ?? 1;
  const norm = gen(p.seed, detail);
  const scale = p.width; // normalized x is 0..1, so scale = width
  const height = norm.bbox.h * scale;
  const scalePoint = (pt: Point): Point => ({ x: pt.x * scale, y: pt.y * height });
  return {
    outline: norm.outline.map(scalePoint),
    parts: norm.parts.map((part) => ({ points: part.points.map(scalePoint) })),
    bbox: { w: scale, h: height },
  };
}
